import {
	IExecuteFunctions,
	INodeExecutionData,
	INodeType,
	INodeTypeDescription,
	NodeConnectionType,
	IHttpRequestMethods,
} from 'n8n-workflow';

import { queueOperations, queueFields } from './CloudflareQueueDescription';

export class CloudflareQueue implements INodeType {
	description: INodeTypeDescription = {
		displayName: 'Cloudflare Queue',
		name: 'cloudflareQueue',
		icon: { light: 'file:cloudflare-queue.svg', dark: 'file:cloudflare-queue.svg' },
		group: ['transform'],
		version: 1,
		subtitle: '={{$parameter["operation"] + ": " + $parameter["resource"]}}',
		description: 'Send and receive messages using Cloudflare Queues (requires paid Workers plan)',
		defaults: {
			name: 'Cloudflare Queue',
		},
		inputs: [NodeConnectionType.Main],
		outputs: [NodeConnectionType.Main],
		usableAsTool: true,
		credentials: [
			{
				name: 'cloudflareApi',
				displayName: 'Cloudflare API key connection',
				required: true,
			},
		],
		properties: [
			{
				displayName: 'Resource',
				name: 'resource',
				type: 'options',
				noDataExpression: true,
				options: [
					{
						name: 'Queue',
						value: 'queue',
					},
					{
						name: 'Message',
						value: 'message',
					},
				],
				default: 'message',
			},
			...queueOperations,
			...queueFields,
		],
	};

	async execute(this: IExecuteFunctions): Promise<INodeExecutionData[][]> {
		const items = this.getInputData();
		const returnData: INodeExecutionData[] = [];
		const resource = this.getNodeParameter('resource', 0) as string;
		const operation = this.getNodeParameter('operation', 0) as string;

		// Get credentials
		const credentials = await this.getCredentials('cloudflareApi');
		const accountId = credentials.accountId as string;
		const apiToken = credentials.apiToken as string;

		const baseURL = `https://api.cloudflare.com/client/v4/accounts/${accountId}/queues`;

		for (let i = 0; i < items.length; i++) {
			try {
				let response;
				const requestOptions: any = {
					headers: {
						Authorization: `Bearer ${apiToken}`,
						'Content-Type': 'application/json',
					},
					method: 'GET' as IHttpRequestMethods,
					uri: '',
					json: true,
				};

				if (resource === 'queue') {
					if (operation === 'list') {
						requestOptions.url = baseURL;
						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: response,
						});
					} else if (operation === 'create') {
						const queueName = this.getNodeParameter('queueName', i) as string;
						const settings = this.getNodeParameter('settings', i, {}) as any;

						requestOptions.method = 'POST';
						requestOptions.url = baseURL;
						requestOptions.body = {
							queue_name: queueName,
							...settings,
						};

						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: response,
						});
					} else if (operation === 'update') {
						const queueId = this.getNodeParameter('queueId', i) as string;
						const settings = this.getNodeParameter('settings', i, {}) as any;

						requestOptions.method = 'PUT';
						requestOptions.url = `${baseURL}/${queueId}`;
						requestOptions.body = settings;

						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: response,
						});
					} else if (operation === 'delete') {
						const queueId = this.getNodeParameter('queueId', i) as string;

						requestOptions.method = 'DELETE';
						requestOptions.url = `${baseURL}/${queueId}`;

						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: {
								success: true,
								queueId,
							},
						});
					} else if (operation === 'getInfo') {
						const queueId = this.getNodeParameter('queueId', i) as string;

						requestOptions.url = `${baseURL}/${queueId}`;
						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: response,
						});
					}
				} else if (resource === 'message') {
					const queueId = this.getNodeParameter('queueId', i) as string;

					if (operation === 'send') {
						const messageBody = this.getNodeParameter('messageBody', i) as string;
						const delaySeconds = this.getNodeParameter('delaySeconds', i, 0) as number;

						requestOptions.method = 'POST';
						requestOptions.url = `${baseURL}/${queueId}/messages`;

						const messageData: any = {
							body: messageBody,
						};

						if (delaySeconds > 0) {
							messageData.delay_seconds = delaySeconds;
						}

						requestOptions.body = [messageData];

						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: response,
						});
					} else if (operation === 'sendBatch') {
						const messages = this.getNodeParameter('messages.message', i, []) as Array<{
							body: string;
							delaySeconds?: number;
						}>;

						requestOptions.method = 'POST';
						requestOptions.url = `${baseURL}/${queueId}/messages`;

						const messagesData = messages.map((msg) => {
							const messageData: any = {
								body: msg.body,
							};
							if (msg.delaySeconds && msg.delaySeconds > 0) {
								messageData.delay_seconds = msg.delaySeconds;
							}
							return messageData;
						});

						requestOptions.body = messagesData;

						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: response,
						});
					} else if (operation === 'pull') {
						const batchSize = this.getNodeParameter('batchSize', i, 10) as number;
						const visibilityTimeout = this.getNodeParameter(
							'visibilityTimeout',
							i,
							30,
						) as number;

						requestOptions.method = 'POST';
						requestOptions.url = `${baseURL}/${queueId}/messages/pull`;
						requestOptions.body = {
							batch_size: batchSize,
							visibility_timeout_ms: visibilityTimeout * 1000, // Convert to milliseconds
						};

						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: response,
						});
					} else if (operation === 'acknowledge') {
						const leaseIds = (this.getNodeParameter('leaseIds', i) as string)
							.split(',')
							.map((id) => id.trim())
							.filter((id) => id.length > 0);

						requestOptions.method = 'POST';
						requestOptions.url = `${baseURL}/${queueId}/messages/ack`;
						requestOptions.body = {
							acks: leaseIds.map((leaseId) => ({ lease_id: leaseId })),
						};

						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: response,
						});
					} else if (operation === 'retry') {
						const leaseIds = (this.getNodeParameter('leaseIds', i) as string)
							.split(',')
							.map((id) => id.trim())
							.filter((id) => id.length > 0);
						const retryDelaySeconds = this.getNodeParameter(
							'retryDelaySeconds',
							i,
							0,
						) as number;

						requestOptions.method = 'POST';
						requestOptions.url = `${baseURL}/${queueId}/messages/ack`;

						const retries = leaseIds.map((leaseId) => {
							const retryData: any = { lease_id: leaseId };
							if (retryDelaySeconds > 0) {
								retryData.retry_delay_seconds = retryDelaySeconds;
							}
							return retryData;
						});

						requestOptions.body = {
							retries,
						};

						response = await this.helpers.httpRequest(requestOptions);
						returnData.push({
							json: response,
						});
					}
				}
			} catch (error: any) {
				// Enhanced error handling for common issues
				let errorMessage = error.message;
				
				// Check for 403 Forbidden (paid plan required)
				if (error.httpCode === '403' || error.message?.includes('403')) {
					errorMessage = `Cloudflare Queues requires a paid Workers plan. Free accounts cannot access Queue APIs. Please upgrade your plan at: https://dash.cloudflare.com/${credentials.accountId}/workers/plans`;
				}
				// Check for other authentication issues
				else if (error.httpCode === '401' || error.message?.includes('401')) {
					errorMessage = `Invalid API token or Account ID. Please verify your Cloudflare API credentials and ensure the token has Queue permissions.`;
				}
				// Check for account/queue not found
				else if (error.httpCode === '404' || error.message?.includes('404')) {
					errorMessage = error.message?.includes('queue') 
						? `Queue not found. Please verify the Queue ID exists in your account.`
						: `Account not found. Please verify your Account ID is correct.`;
				}

				if (this.continueOnFail()) {
					returnData.push({
						json: {
							error: errorMessage,
							originalError: error.message,
							httpCode: error.httpCode,
						},
						pairedItem: {
							item: i,
						},
					});
					continue;
				}
				
				// Create enhanced error for throw
				const enhancedError = new Error(errorMessage);
				(enhancedError as any).httpCode = error.httpCode;
				(enhancedError as any).originalError = error.message;
				throw enhancedError;
			}
		}

		return [returnData];
	}
}
